'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { HorizontalSteps } from '../../../components/HorizontalSteps';
import { Step1PersonalData, Step2LinkVerification, Step3ContentUpload } from './steps';
import { Step1FormData, Step2FormData } from './validation';
import { extractModelNameFromOnlyFansUrl } from './utils';
import clientAccService from '../../../api/client/clientAccService';
import clientAuthService from '../../../api/client/clientAuthService';
import { useAuth } from '../../../contexts/AuthContext';
import { Button, useDisclosure } from '@heroui/react';
import { ErrorBlock, CodeVerificationModal } from './components';
import { CustomHeroButton } from '../../../components';
import { useAccount } from '../../../contexts/AccountContext';
import { useTranslations } from '../../../contexts/translation.context';
import AlertBlock from '../../../components/alert-block';
const CreateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { refreshAccounts } = useAccount();
  const { t } = useTranslations();
  // Если у пользователя уже есть tg_tag, начинаем с 1 шага (но показываем как 2-й)
  const initialStep = 1;
  const [step, setStep] = useState(initialStep);
  const [step1Data, setStep1Data] = useState<Step1FormData>({
    telegram: user?.tg_tag || '',
    contact: user?.whats_app || '',
  });

  const [step2Data, setStep2Data] = useState<Step2FormData>({ ofLink: '' });
  const [isLinkVerified, setIsLinkVerified] = useState(false);
  const [isCheckingLink, setIsCheckingLink] = useState(false);
  const [isCheckingCode, setIsCheckingCode] = useState(false);
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [generatedCode, setGeneratedCode] = useState(''); // Код будет получен с сервера
  const [modelData, setModelData] = useState(''); // Код будет получен с сервера
  const [step1Valid, setStep1Valid] = useState(false);
  const [forceValidation, setForceValidation] = useState(false);

  // Ключи для localStorage
  const MODEL_DATA_KEY = 'createAccount_modelData';
  const MODEL_ID_KEY = 'createAccount_modelId';
  const LINK_VERIFIED_KEY = 'createAccount_linkVerified';
  const CODE_VERIFIED_KEY = 'createAccount_codeVerified';
  const CODE_KEY = 'createAccount_code';
  const [step2Valid, setStep2Valid] = useState(false);
  const [error, setError] = useState('');
  const [fileValidationErrors, setFileValidationErrors] = useState<string[]>([]);
  const [isSubmittingStep1, setIsSubmittingStep1] = useState(false);
  const [isSubmittingStep3, setIsSubmittingStep3] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [modelId, setModelId] = useState<string | undefined>(undefined);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const [uploadedFilesCount, setUploadedFilesCount] = useState(0);
  // Для загрузки изображений будет отдельный стейт позже
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isModalSuccess, setIsModalSuccess] = useState(false);
  const addMoreFilesRef = useRef<(() => void) | null>(null);
  const startUploadRef = useRef<(() => void) | null>(null);
  // Функция для сохранения данных модели в localStorage
  const saveModelDataToStorage = (
    data: any,
    modelId: string,
    linkVerified: boolean,
    codeVerified: boolean
  ) => {
    try {
      // localStorage.setItem(MODEL_DATA_KEY, JSON.stringify(data));
      localStorage.setItem(MODEL_ID_KEY, modelId);
      // localStorage.setItem(LINK_VERIFIED_KEY, JSON.stringify(linkVerified));
      // localStorage.setItem(CODE_VERIFIED_KEY, JSON.stringify(codeVerified));
      // localStorage.setItem(CODE_KEY, JSON.stringify(data.code));
    } catch (error) {
      console.error('Error saving data to localStorage:', error);
    }
  };

  // Функция для загрузки данных модели из localStorage
  const loadModelDataFromStorage = () => {
    try {
      const savedModelData = localStorage.getItem(MODEL_DATA_KEY);
      const savedModelId = localStorage.getItem(MODEL_ID_KEY);
      const savedLinkVerified = localStorage.getItem(LINK_VERIFIED_KEY);
      const savedCodeVerified = localStorage.getItem(CODE_VERIFIED_KEY);
      const savedCode = localStorage.getItem(CODE_KEY);
      if (savedCode) {
        setGeneratedCode(JSON.parse(savedCode));
      }
      if (savedModelData) {
        setModelData(JSON.parse(savedModelData));
      }
      if (savedModelId) {
        setModelId(savedModelId);
      }
      if (savedLinkVerified) {
        setIsLinkVerified(JSON.parse(savedLinkVerified));
      }
      if (savedCodeVerified) {
        setIsCodeVerified(JSON.parse(savedCodeVerified));
      }
    } catch (error) {
      console.error('Error loading data from localStorage:', error);
    }
  };

  // Функция для очистки данных модели из localStorage
  const clearModelDataFromStorage = () => {
    try {
      localStorage.removeItem(MODEL_DATA_KEY);
      localStorage.removeItem(MODEL_ID_KEY);
      localStorage.removeItem(LINK_VERIFIED_KEY);
      localStorage.removeItem(CODE_VERIFIED_KEY);
    } catch (error) {
      console.error('Error clearing data from localStorage:', error);
    }
  };

  // Мемоизируем breadcrumbs чтобы избежать бесконечных перерендеров
  const breadcrumbs = useMemo(
    () => [
      { label: t('createAccount.breadcrumb.home'), href: '/' },
      { label: t('createAccount.breadcrumb.accounts'), href: '/client/accounts' },
      { label: t('createAccount.breadcrumb.addNewAccount'), isActive: true },
    ],
    [t]
  );

  const instructionAlertData = useMemo(
    () => [
      {
        severity: 'info' as const,
        title: t('createAccount.instructions.title'),
        descriptions: [
          t('createAccount.instructions.step1'),
          t('createAccount.instructions.step2'),
          t('createAccount.instructions.step3'),
          t('createAccount.instructions.step4'),
          t('createAccount.instructions.step5'),
          t('createAccount.instructions.step6'),
        ],
      },
    ],
    [t]
  );

  // Устанавливаем breadcrumbs для этой страницы
  usePageBreadcrumbs(breadcrumbs);

  // Загружаем данные модели из localStorage при первом рендере
  useEffect(() => {
    loadModelDataFromStorage();
  }, []);

  // Синхронизация с URL параметрами
  useEffect(() => {
    const stepParam = searchParams.get('step');
    if (stepParam) {
      const stepNumber = parseInt(stepParam);
      if (stepNumber >= 1 && stepNumber <= 3) {
        setStep(stepNumber);
      }
    } else {
      // Если нет URL параметра, устанавливаем начальный шаг
      setStep(1);
      navigate('?step=1', { replace: true });
    }
  }, [searchParams, user, navigate]);

  const updateStep = (newStep: number) => {
    setStep(newStep);
    setError(''); // Очищаем ошибки при смене шага
    if ((!user?.tg_tag && newStep !== 3) || (user?.tg_tag && newStep !== 2)) {
      setUploadCompleted(false); // Сбрасываем состояние загрузки при переходе на другие шаги
    }
    // Если переходим на первый шаг, очищаем данные модели
    if (newStep === 1) {
      clearModelDataFromStorage();
      setIsLinkVerified(false);
      setIsCodeVerified(false);
      setModelData('');
      setModelId(undefined);
    }
    navigate(`?step=${newStep}`, { replace: true });
  };

  const handleContinue = async () => {
    if (!user?.tg_tag && step < 3) {
      if (step === 1) {
        if (step1Valid) {
          try {
            await handleStep1Submit(step1Data);
            updateStep(step + 1);
          } catch (error) {
            console.log('Failed to proceed to next step due to error');
          }
        } else {
          // Принудительно триггерим валидацию, показывая ошибки
          setForceValidation(true);
        }
      } else if (step === 2 && step2Valid && isLinkVerified && isCodeVerified) {
        updateStep(step + 1);
      }
    } else if (user?.tg_tag && step < 2) {
      if (step === 1 && step2Valid && isLinkVerified && isCodeVerified) {
        updateStep(step + 1);
      }
    } else if ((!user?.tg_tag && step === 3) || (user?.tg_tag && step === 2)) {
      console.log('Account created successfully');
      clearModelDataFromStorage();
      navigate('/client/accounts');
    }
  };

  const handleCheckLink = async () => {
    console.log('handleCheckLink вызван');
    console.log('step2Data.ofLink:', step2Data.ofLink);

    if (!step2Data.ofLink.trim()) {
      console.log('Ссылка пустая');
      return;
    }

    const modelName = extractModelNameFromOnlyFansUrl(step2Data.ofLink);
    console.log('Извлеченное имя модели:', modelName);

    if (!modelName) {
      setError('Некорректная ссылка OnlyFans. Пожалуйста, проверьте формат ссылки.');
      return;
    }

    setError(''); // Очищаем предыдущие ошибки

    setIsCheckingLink(true);
    console.log('Начинаем проверку ссылки для модели:', modelName);

    try {
      // Создаем код подтверждения для модели
      const response = await clientAccService.createConfirmCode(modelName);
      console.log('Ответ от createConfirmCode:', response);
      setModelData(response.data);
      setModelId(response.data.of_user_id);
      // Предполагаем, что сервер возвращает сгенерированный код
      if (response.data && response.data.code) {
        setGeneratedCode(response.data.code);
      } else {
        // Если сервер не возвращает код, генерируем временный
        setGeneratedCode('bazinga023152');
      }

      setIsLinkVerified(true);
      setError(''); // Очищаем ошибки при успехе

      // Сохраняем данные модели в localStorage
      saveModelDataToStorage(response.data, response.data.of_user_id, true, false);

      console.log('Ссылка успешно проверена');
    } catch (error: any) {
      console.error('Ошибка при создании кода подтверждения:', error);
      setError(error?.response?.data?.detail || t('createAccount.errors.accountNotFound'));
    } finally {
      setIsCheckingLink(false);
    }
  };

  const handleCheckCode = async () => {
    const modelName = extractModelNameFromOnlyFansUrl(step2Data.ofLink);
    if (!modelName) {
      setError('Некорректная ссылка OnlyFans. Пожалуйста, проверьте формат ссылки.');
      return;
    }

    setError(''); // Очищаем предыдущие ошибки

    setIsCheckingCode(true);
    console.log('handleCheckCode');
    console.log('modelName', modelName);
    try {
      // Проверяем код подтверждения для модели
      await clientAccService.checkConfirmCode(modelName);
      setIsCodeVerified(true);
      // Устанавливаем modelId как modelName для использования в загрузке файлов
      // setModelId(modelName);
      setError(''); // Очищаем ошибки при успехе

      // Сохраняем обновленные данные модели в localStorage
      // saveModelDataToStorage(modelData, modelName, true, true);

      // Показываем модальное окно успеха
      setIsModalSuccess(true);
      onOpen();
    } catch (error: any) {
      setError(error?.response?.data?.detail || null);
      console.error('Ошибка при проверке кода:', error);
      // Показываем модальное окно ошибки
      setIsModalSuccess(false);
      onOpen();
    } finally {
      setIsCheckingCode(false);
    }
  };

  const handleStep1Submit = async (values: Step1FormData) => {
    try {
      setIsSubmittingStep1(true);
      setError('');

      // Отправляем данные на сервер через updateProfile
      await clientAuthService.updateProfile({
        tg_tag: values.telegram,
        whats_app: values.contact,
      });
      setStep1Data(values);
      console.log('Профиль обновлен и аккаунт создан успешно');
    } catch (error) {
      console.error('Ошибка при обновлении профиля или создании аккаунта:', error);
      setError(t('createAccount.errors.dataSaveError'));
      throw error; // Пробрасываем ошибку, чтобы handleContinue мог ее обработать
    } finally {
      setIsSubmittingStep1(false);
    }
  };

  const handleStep1FormChange = (values: Step1FormData) => {
    setStep1Data(values);
    // Очищаем ошибку при изменении данных формы
    if (error) {
      setError('');
    }
    // Сбрасываем принудительную валидацию при изменении полей
    if (forceValidation) {
      setForceValidation(false);
    }
  };

  const handleStep2Submit = (values: Step2FormData) => {
    setStep2Data(values);
  };

  const handleAddMoreFiles = () => {
    if (addMoreFilesRef.current) {
      addMoreFilesRef.current();
    }
  };

  const handleFileValidationError = (errors: string[]) => {
    setFileValidationErrors(errors);
  };

  const handleSave = async () => {
    if (uploadedFilesCount >= 3) {
      setIsUploading(true);
      // Начинаем загрузку файлов
      if (startUploadRef.current) {
        startUploadRef.current();
      }

      // setUploadCompleted будет вызван через onUploadComplete после завершения загрузки
      // console.log('Начинаем загрузку файлов...');
    }
  };

  return (
    <div className="px-4 py-6 md:py-4 md:px-6">
      {/* Page Title */}
      <h1 className="text-xl text-2xl font-medium md:font-semibold text-gray-900 mb-8 md:mb-4">
        {t('createAccount.title')}
      </h1>
      {/* Stepper */}
      <div className="mb-6 md:mb-8 md:mx-0 -mx-4 md:px-0 px-4 max-md:overflow-x-auto scroller-none">
        <HorizontalSteps
          currentStep={user?.tg_tag ? step - 1 : step - 1}
          steps={
            user?.tg_tag
              ? [
                  {
                    title: (
                      <div className="flex flex-col items-start min-w-[11.1875rem]">
                        <h3 className="text-base leading-6 text-auth-ink font-medium">
                          {t('createAccount.steps.link.title')}
                        </h3>
                        <p className="text-sm max-w-[13rem] text-auth-muted leading-5 text-start">
                          {t('createAccount.steps.link.description')}
                        </p>
                      </div>
                    ),
                  },
                  {
                    title: (
                      <div className="flex flex-col items-start min-w-[11.1875rem]">
                        <h3 className="text-base leading-6 text-auth-ink font-medium">
                          {t('createAccount.steps.content.title')}
                        </h3>
                        <p className="text-sm max-w-[13rem] text-auth-muted leading-5 text-start">
                          {t('createAccount.steps.content.description')}
                        </p>
                      </div>
                    ),
                  },
                ]
              : [
                  {
                    title: (
                      <div className="flex flex-col items-start">
                        <h3 className="text-base leading-6 text-auth-ink font-medium">
                          {t('createAccount.steps.data.title')}
                        </h3>
                        <p className="text-sm max-w-[13rem] text-auth-muted leading-5 text-start">
                          {t('createAccount.steps.data.description')}
                        </p>
                      </div>
                    ),
                  },
                  {
                    title: (
                      <div className="flex flex-col items-start">
                        <h3 className="text-base leading-6 text-auth-ink font-medium">
                          {t('createAccount.steps.link.title')}
                        </h3>
                        <p className="text-sm max-w-[13rem] text-auth-muted leading-5 text-start">
                          {t('createAccount.steps.link.description')}
                        </p>
                      </div>
                    ),
                  },
                  {
                    title: (
                      <div className="flex flex-col items-start">
                        <h3 className="text-base leading-6 text-auth-ink font-medium">
                          {t('createAccount.steps.content.title')}
                        </h3>
                        <p className="text-sm max-w-[13rem] text-auth-muted leading-5 text-start">
                          {t('createAccount.steps.content.description')}
                        </p>
                      </div>
                    ),
                  },
                ]
          }
          color="primary"
          className="pointer-events-none"
        />
      </div>
      {/* Error Block */}

      {/* Form Content */}
      <div className="">
        {step === 1 && !user?.tg_tag && (
          <>
            <Step1PersonalData
              initialValues={step1Data}
              onSubmit={handleStep1FormChange}
              onValidationChange={setStep1Valid}
              forceValidation={forceValidation}
            />
            {error && (
              <div className="w-full">
                <ErrorBlock message={error} className="mb-4" />
              </div>
            )}
            <CustomHeroButton
              title={t('createAccount.buttons.continue')}
              className="mt-6"
              onPress={() => {
                console.log('Button clicked');
                handleContinue();
              }}
            />
          </>
        )}
        {((step === 1 && user?.tg_tag) || (step === 2 && !user?.tg_tag)) && (
          <>
            <Step2LinkVerification
              initialValues={step2Data}
              onSubmit={handleStep2Submit}
              onValidationChange={setStep2Valid}
              isLinkVerified={isLinkVerified}
              isCheckingLink={isCheckingLink}
              isCheckingCode={isCheckingCode}
              isCodeVerified={isCodeVerified}
              generatedCode={generatedCode}
              modelData={modelData}
              onCheckLink={handleCheckLink}
              onCheckCode={handleCheckCode}
            />
            {error && (
              <div className="w-full">
                <ErrorBlock message={error} className="mt-4" />
              </div>
            )}
            <CodeVerificationModal
              isOpen={isOpen}
              error={error}
              onClose={
                isModalSuccess
                  ? () => {
                      clearModelDataFromStorage();
                      const nextStep = user?.tg_tag ? 2 : 3;
                      setStep(nextStep);
                      navigate(`?step=${nextStep}`, { replace: true });
                    }
                  : onClose
              }
              isSuccess={isModalSuccess}
            />
            {/* Кнопки для второго шага под блоком */}
            <div className="mt-6 flex gap-4">
              {!isLinkVerified && (
                <CustomHeroButton
                  title={
                    isCheckingLink
                      ? t('createAccount.buttons.checking')
                      : t('createAccount.buttons.verify')
                  }
                  onPress={handleCheckLink}
                  isLoading={isCheckingLink}
                  disabled={!step2Data.ofLink.trim()}
                />
              )}

              {isLinkVerified && !isCodeVerified && (
                <CustomHeroButton
                  title={
                    isCheckingCode
                      ? t('createAccount.buttons.confirming')
                      : t('createAccount.buttons.confirmCode')
                  }
                  onPress={handleCheckCode}
                  isLoading={isCheckingCode}
                  disabled={!step2Data.ofLink.trim()}
                />
              )}
            </div>
          </>
        )}
        {((step === 2 && user?.tg_tag) || (step === 3 && !user?.tg_tag)) && (
          <>
            <Step3ContentUpload
              modelId={modelId}
              onUploadComplete={() => {
                console.log('Upload completed');
                setUploadCompleted(true);
                setIsUploading(false);
                // Обновляем список аккаунтов
                refreshAccounts();
              }}
              onAddMoreFiles={addMoreFilesRef}
              onStartUpload={startUploadRef}
              onValidationError={handleFileValidationError}
              onModalClose={() => {
                clearModelDataFromStorage();
                navigate('/client/accounts');
              }}
              onFilesCountChange={(count) => {
                setUploadedFilesCount(count);
                // Если файлов меньше 3, сбрасываем uploadCompleted
                if (count < 3) {
                  setUploadCompleted(false);
                }
              }}
            />
            {error && (
              <div className="w-full">
                <ErrorBlock message={error} className="mb-4" />
              </div>
            )}
            {fileValidationErrors.length > 0 && (
              <div className="w-full">
                <ErrorBlock message={fileValidationErrors.join('\n')} className="mt-4" />
              </div>
            )}
            <div className="pt-4 flex gap-4">
              {uploadedFilesCount > 0 && uploadedFilesCount < 6 && (
                <CustomHeroButton
                  title={t('createAccount.buttons.addMorePhotos')}
                  onPress={handleAddMoreFiles}
                  isLoading={isSubmittingStep3}
                />
              )}
              <CustomHeroButton
                title={
                  isUploading
                    ? t('createAccount.buttons.uploading')
                    : t('createAccount.buttons.save')
                }
                onPress={handleSave}
                isLoading={isUploading}
                disabled={uploadedFilesCount < 3}
              />
            </div>
          </>
        )}
        {/* Кнопки управления шагами */}
      </div>
      <div className="mt-4 md:mt-8">
        <AlertBlock data={instructionAlertData} />
      </div>
    </div>
  );
};

export default CreateAccountPage;
